<template>
  <div :class="['SanitasTerms', { isOv }]">
    <!-- Body -->
    <div :class="['body', { animateReminder, isOv }]">
      <div :class="['container', { isOv }]">
        <div v-if="!isOv" class="title">
          <h1>{{ title }}</h1>
          <div class="text" v-html="introduction" />
        </div>
        <div v-if="fetching" class="loading">
          <Spinner class="theme--green" />
        </div>
        <div v-if="fetchFailed" class="title">
          <div class="text is-error">
            {{ label('terms_error') }}
          </div>
        </div>

        <div v-if="!fetching" :class="['tiles-container', { isOv }]">
          <Tile
            v-for="term in displayedTerms"
            :key="term.id"
            class="tile"
            :read-only="readOnly"
            :type="term.type"
            :version="term.version"
            :mode="term.accepted || readOnly ? 'accepted' : 'open'"
            :title="term.content.title"
            :date="term.content.date"
            :content="term.content.terms"
            :introduction="term.content.introduction"
            :is-ov="isOv"
            @sanitas_terms-tile_tap="openTerm"
          />
          <template v-if="isOv">
            <Checkbox
              v-for="term in displayedTerms"
              :key="term.type"
              v-model="term.accepted"
              class="checkbox"
              :text="term.content.accept"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div :class="['footer', { isOv, readOnly, reminder }]">
      <div class="container">
        <SanitasButton v-if="readOnly" primary full-width-in-xs @click="back">
          {{ label('terms_button_back') }}
        </SanitasButton>
        <template v-else>
          <SanitasButton v-if="!reminder" secondary @click="back">
            {{ label('terms_button_back') }}
          </SanitasButton>
          <div v-if="!reminder" class="spacer" />
          <SanitasButton
            primary
            :disabled="!readOnly && !termsAccepted"
            :is-spinner-active="updating"
            @click="next"
          >
            {{ nextButtonLabel }}
          </SanitasButton>
        </template>
      </div>
    </div>

    <SanitasModal
      v-if="modal"
      close-icon
      :close-on-mask-click="false"
      @close="modal = false"
    >
      <Detail
        :title="selectedTerm.content.title"
        :version="selectedTerm.content.version"
        :date="selectedTerm.content.date"
        :content="selectedTerm.content.terms"
        :read-only="readOnly"
        @accept="onAccept(selectedTerm)"
        @decline="onDecline(selectedTerm)"
        @close="modal = false"
      />
    </SanitasModal>
  </div>
</template>

<script>
import './assets/typography.pcss'
import './assets/base-styles.pcss'

import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'
import { computed, onMounted, ref } from '@vue/composition-api'

import Checkbox from './components/Checkbox'
import Detail from './components/Detail'
import SanitasButton from './components/SanitasButton'
import SanitasModal from './components/SanitasModal'
import Spinner from './components/Spinner'
import Tile from './components/Tile'
import {
  loadLabels,
  loadTermsConfig,
  loadTermsContent,
} from './services/backend'

export default {
  name: 'SanitasTerms',

  components: {
    Checkbox,
    Spinner,
    SanitasButton,
    Tile,
    SanitasModal,
    Detail,
  },

  props: {
    language: {
      type: String,
      default: 'de',
    },

    readOnly: {
      type: Boolean,
      default: true,
    },

    // used for online verification
    isOv: Boolean,

    backLink: {
      type: String,
      default: '',
    },

    contentBaseUrl: {
      type: String,
      default: '',
    },

    termsData: {
      type: Object,
      default: null,
    },
  },

  setup(props, context) {
    const showKey = ref(false)
    window.i36n = {
      toggleKeys: function() {
        console.log(showKey.value)
        showKey.value = !showKey.value
      },
    }

    provideI36n(props.language, { load: loadLabels, showKey })
    const { $label: label } = useI36n()

    const reminder = ref(false)
    const terms = ref([])
    const featureTerms = ref([])
    const fetching = ref(true)
    const fetchFailed = ref(false)
    const updating = ref(false)
    const modal = ref(false)
    const animateReminder = ref(false)
    const selectedTerm = ref({ content: {} })

    const title = computed(() =>
      reminder.value
        ? label.value('terms_confirmation_title')
        : label.value('terms_main_title'),
    )

    const introduction = computed(() =>
      reminder.value
        ? label.value('terms_confirmation_introduction')
        : label.value('terms_main_introduction'),
    )

    const nextButtonLabel = computed(() =>
      reminder.value
        ? label.value('terms_button_save')
        : label.value('terms_button_next'),
    )

    const view = computed(() => (reminder.value ? 'reminder' : 'main'))

    const displayedTerms = computed(() => {
      return reminder.value
        ? featureTerms.value
        : [...terms.value, ...featureTerms.value]
    })

    const termsAccepted = computed(() => {
      return terms.value.every(term => term.accepted)
    })

    const featureTermsAccepted = computed(() => {
      return featureTerms.value.every(term => term.accepted)
    })

    const openTerm = event => {
      selectedTerm.value = [...terms.value, ...featureTerms.value].find(
        t => t.type === event.type,
      )

      modal.value = true
    }

    const onAccept = term => {
      term.accepted = true
      term.view = view.value
      modal.value = false
    }

    const onDecline = term => {
      term.accepted = false
      term.view = view.value
      modal.value = false
    }

    const next = () => {
      const mapView = term => ({
        ...term,
        ...{ view: term.view || 'main' },
      })

      if (
        termsAccepted.value &&
        (featureTermsAccepted.value || reminder.value)
      ) {
        updating.value = true
        context.emit('submit', {
          terms: terms.value.map(mapView),
          featureTerms: featureTerms.value.map(mapView),
        })
      } else {
        animateReminder.value = true
        window.setTimeout(() => {
          reminder.value = true
        }, 500)
      }
    }

    const back = () => {
      if (props.readOnly && props.backLink) {
        window.location.href = props.backLink
      } else {
        context.emit('back')
      }
    }

    const clearSubmit = () => {
      updating.value = false
    }

    onMounted(async () => {
      try {
        const fetchData = props.termsData
          ? () => Promise.resolve(props.termsData)
          : loadTermsConfig

        const data = await fetchData()
        const calls = Object.keys(data).reduce((acc, key) => {
          return acc.concat(
            data[key].map((k, i) =>
              loadTermsContent(
                props.contentBaseUrl,
                key,
                k.type,
                k.version,
                props.language,
              ).then(content =>
                Object.assign({}, data[key][i], {
                  key,
                  content,
                }),
              ),
            ),
          )
        }, [])

        const responses = await Promise.all(calls)
        responses.forEach(response => {
          if (response.key === 'terms') {
            terms.value.push(response)
          } else if (response.key === 'featureTerms') {
            featureTerms.value.push(response)
          }
        })
      } catch (e) {
        fetchFailed.value = true
      } finally {
        fetching.value = false
      }
    })

    return {
      fetching,
      fetchFailed,
      updating,
      modal,
      label,
      selectedTerm,
      introduction,
      nextButtonLabel,
      animateReminder,
      reminder,
      title,
      view,
      featureTerms,
      terms,
      displayedTerms,
      termsAccepted,
      featureTermsAccepted,
      openTerm,
      onAccept,
      onDecline,
      next,
      back,
      clearSubmit,
    }
  },
}
</script>

<style lang="postcss" scoped>
:root {
  --footer-height: 70px;
}

@keyframes switch-screen {
  0% {
    opacity: 1;
  }
  40% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.SanitasTerms {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 400px;
  font-family: var(--font-family-open-sans);
  font-weight: 100;
  color: var(--c-black);

  &.isOv {
    min-width: auto;
    overflow-y: scroll;
    padding-bottom: var(--footer-height);
    position: static;
  }
}

.loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.container {
  margin: 0 auto;
  padding: 0 25px;
  max-width: 940px;

  &.isOv {
    width: 100%;
    padding: 0;

    @media (--xs) {
      padding: 0 25px;
    }
  }

  @media (--md) {
    padding: 0;
  }
}

.header {
  height: 60px;
  background-color: var(--c-white);

  @media (--md) {
    height: 90px;
  }

  .logo {
    position: absolute;
    top: 10px;
    left: 25px;
    width: 95px;
    height: 16px;
    transition: width 0.2s, height 0.2s, left 0.2s, top 0.2s;
    transition-timing-function: linear;

    @media (--md) {
      width: 122px;
      height: 30px;
      top: 30px;
      left: 30px;
    }

    @media (--lg) {
      left: 30px;
    }
  }
}

.body {
  flex: 1 0 auto;
  background-color: var(--c-warm-grey);

  &.isOv {
    height: calc(100% - var(--footer-height));
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: 10px;
      height: 4px;
      background: var(--c-dark-grey);
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--c-light-green);
      -webkit-border-radius: 1ex;
    }
  }

  .title {
    font-size: 25px;
    line-height: 1.4;
    margin: 50px auto !important;

    h1 {
      margin: 20px 0;
      font-family: var(--font-family-open-sans);
      font-weight: 100;
      color: var(--c-black);
    }
  }
}

.tiles-container {
  display: flex;
  justify-content: center;
  margin: 30px 0;
  flex-wrap: wrap;

  &.isOv {
    flex-direction: column;
    margin: 0;

    .tile {
      max-width: none;
      margin: 0;
      width: 100%;
      word-break: break-word;
    }
  }

  .animateReminder & {
    animation: switch-screen 1s;
  }

  .tile {
    max-width: 510px;
    margin: 0 0 20px 0;
  }

  @media (min-width: 940px) {
    justify-content: space-between;
    min-width: 510px;

    .tile {
      max-width: calc(50% - 10px);
      min-width: calc(50% - 10px);
      margin-left: 0;
      margin-right: 0;
    }
  }
}

.checkbox {
  margin-bottom: 20px;
  font-weight: bold;
  padding: 0 20px;
  word-break: break-word;
}

.footer {
  right: 0;
  padding: 20px 25px 40px;
  display: flex;
  align-items: center;
  height: 90px;
  background-color: var(--c-warm-grey);

  &.isOv {
    height: auto;
    background-color: transparent;
    padding: 20px 0 0 0;
    position: absolute;
    bottom: 0;
    width: 100%;
  }

  .container {
    text-align: center;
  }

  &.readOnly {
    justify-content: center;
  }

  &:not(.readOnly) .container {
    padding: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
  }

  &:not(.readOnly).reminder .container {
    justify-content: center;

    @media (--md) {
      justify-content: flex-end;
    }
  }
}
</style>
